import { format } from 'date-fns';

/**
 * Converts a local date to an expanded UTC range for database queries.
 * This ensures we capture all games that could appear on that local date,
 * regardless of timezone differences.
 * 
 * Example: User in Portugal (UTC+1) viewing Oct 6
 * - Local day starts: Oct 6 00:00 (UTC: Oct 5 23:00)
 * - Local day ends: Oct 7 00:00 (UTC: Oct 6 23:00)
 * - We expand range by ±1 day to handle extreme timezones (UTC+14 to UTC-12)
 * 
 * @param dateFrom - Start date in 'yyyy-MM-dd' format
 * @param dateTo - End date in 'yyyy-MM-dd' format (exclusive)
 * @returns Expanded UTC range { utcFrom, utcTo } in ISO format
 */
export function getUTCRangeForLocalDate(dateFrom: string, dateTo: string) {
  // Parse the local dates - force LOCAL timezone interpretation
  // Using replace(/-/g, '/') ensures consistent local midnight interpretation across browsers
  const fromDate = new Date(dateFrom.replace(/-/g, '/'));
  const toDate = new Date(dateTo.replace(/-/g, '/'));
  
  // Expand range by ±1 day to cover all possible timezones
  // This handles edge cases like UTC+14 (earliest) and UTC-12 (latest)
  const expandedFrom = new Date(fromDate);
  expandedFrom.setDate(expandedFrom.getDate() - 1);
  
  const expandedTo = new Date(toDate);
  expandedTo.setDate(expandedTo.getDate() + 1);
  
  // Convert to ISO strings for Supabase query
  return {
    utcFrom: expandedFrom.toISOString(),
    utcTo: expandedTo.toISOString()
  };
}

/**
 * Checks if a game (with UTC timestamp) falls within a local date range.
 * Converts the game's UTC timestamp to local timezone before checking.
 * 
 * @param gameMatchDate - Game's match_date from database (UTC timestamp string)
 * @param dateFrom - Local date range start in 'yyyy-MM-dd' format
 * @param dateTo - Local date range end in 'yyyy-MM-dd' format (exclusive)
 * @returns true if game falls within the local date range
 */
export function isGameInLocalDateRange(
  gameMatchDate: string,
  dateFrom: string,
  dateTo: string
): boolean {
  // Convert UTC timestamp to local date string
  const gameLocalDate = format(new Date(gameMatchDate), 'yyyy-MM-dd');
  
  // Check if local date is within range
  return gameLocalDate >= dateFrom && gameLocalDate < dateTo;
}

/**
 * Formats a date object to 'yyyy-MM-dd' string.
 * 
 * @param date - Date object
 * @returns Date string in 'yyyy-MM-dd' format
 */
export function formatLocalDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Checks if a scheduled game is about to start (kickoff soon).
 * Returns true if the game starts within the specified threshold.
 * 
 * @param matchDate - Game's match_date (UTC timestamp string or Date)
 * @param thresholdMinutes - Minutes before kickoff to trigger (default: 30)
 * @returns true if game starts within threshold minutes
 */
export function isKickoffSoon(matchDate: string | Date, thresholdMinutes = 30): boolean {
  const kickoff = new Date(matchDate);
  const now = new Date();
  const diffMs = kickoff.getTime() - now.getTime();
  const diffMinutes = diffMs / (1000 * 60);
  return diffMinutes > 0 && diffMinutes <= thresholdMinutes;
}

/**
 * Formats game elapsed time to show injury/stoppage time correctly.
 * Examples: 90 elapsed + 2 extra becomes "90+2'", 45 elapsed + 3 extra becomes "45+3'"
 * 
 * @param elapsed - Base elapsed minutes from game start (45, 90, etc.)
 * @param statusShort - Game status short code (1H, 2H, HT, ET, etc.)
 * @param elapsedExtra - Injury/stoppage time minutes from API (if available)
 * @returns Formatted time string (e.g., "90+2'", "45'", "122'")
 */
export function formatGameTime(
  elapsed: number | undefined, 
  statusShort?: string,
  elapsedExtra?: number | null
): string {
  if (!elapsed) return "0'";
  
  // Only show stoppage time format when it makes sense for the current period
  if (elapsedExtra && elapsedExtra > 0) {
    // First half stoppage: only when elapsed is exactly 45
    if (statusShort === '1H' && elapsed === 45) {
      return `45+${elapsedExtra}'`;
    }
    // Second half stoppage: only when elapsed is 90 or more
    if (statusShort === '2H' && elapsed >= 90) {
      return `90+${elapsedExtra}'`;
    }
    // Extra time first half stoppage: only when elapsed is exactly 105
    if (statusShort === 'ET' && elapsed === 105) {
      return `105+${elapsedExtra}'`;
    }
    // Extra time second half stoppage: only when elapsed is 120 or more
    if (statusShort === 'ET' && elapsed >= 120) {
      return `120+${elapsedExtra}'`;
    }
  }
  
  // Regular minutes for all other cases
  return `${elapsed}'`;
}
